import { useEffect, useRef, useState } from "react";

/**
 * <SectionTransition variant="scanline" />
 *
 * Creative scroll-triggered dividers between sections.
 * All variants are theme-aware (uses CSS vars / cyan accent), RTL-safe,
 * respect prefers-reduced-motion, and kept short (~80-120px vertical).
 *
 * Variants:
 *  - scanline    horizontal scan bar + data ticks
 *  - orbit       drawing orbital arc with pulse nodes
 *  - wave        flowing SVG wave that draws itself
 *  - particles   floating cyan particles drifting upward
 *  - diagonal    sharp diagonal slash with glitch ticks
 *  - pulse       radar-style concentric pulse rings
 */
export const SectionTransition = ({ variant = "scanline", className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.3, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`section-transition-${variant}`}
      aria-hidden="true"
      className={`relative w-full pointer-events-none select-none ${className}`}
      style={{ height: variant === "particles" ? 140 : 110 }}
    >
      {variant === "scanline" && <Scanline visible={visible} />}
      {variant === "orbit" && <Orbit visible={visible} />}
      {variant === "wave" && <Wave visible={visible} />}
      {variant === "particles" && <Particles visible={visible} />}
      {variant === "diagonal" && <Diagonal visible={visible} />}
      {variant === "pulse" && <Pulse visible={visible} />}
    </div>
  );
};

/* ---------- Scanline ---------- */
const Scanline = ({ visible }) => (
  <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
    {/* Base hairline */}
    <div className="absolute left-1/2 -translate-x-1/2 h-px w-[min(1100px,92%)] bg-gradient-to-r from-transparent via-[var(--border-soft)] to-transparent" />
    {/* Sweeping cyan bar */}
    <div
      className="absolute top-1/2 -translate-y-1/2 h-[2px] w-[18%]"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(0,229,255,0.0) 10%, rgba(0,229,255,0.9) 50%, rgba(0,229,255,0.0) 90%, transparent 100%)",
        boxShadow: "0 0 24px rgba(0,229,255,0.6)",
        animation: visible ? "gtl-scan 2.6s cubic-bezier(0.22,1,0.36,1) infinite" : "none",
      }}
    />
    {/* Data ticks */}
    <div className="absolute left-1/2 -translate-x-1/2 w-[min(1100px,92%)] flex items-center justify-between">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className="block w-px bg-cyan-400/60"
          style={{
            height: i === 2 || i === 3 ? 14 : 6,
            opacity: visible ? 0.7 : 0,
            transform: visible ? "scaleY(1)" : "scaleY(0)",
            transition: `all 700ms cubic-bezier(0.22,1,0.36,1) ${i * 80}ms`,
            transformOrigin: "center",
          }}
        />
      ))}
    </div>
    {/* Telemetry label */}
    <span
      className="absolute top-1 left-1/2 -translate-x-1/2 text-[9px] mono-label text-cyan-400/70 tracking-[0.3em]"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate(-50%, 0)" : "translate(-50%, -8px)",
        transition: "all 800ms cubic-bezier(0.22,1,0.36,1) 300ms",
      }}
    >
      — SECTION BREAK —
    </span>
  </div>
);

/* ---------- Orbit ---------- */
const Orbit = ({ visible }) => (
  <div className="absolute inset-0 flex items-center justify-center">
    <svg width="180" height="90" viewBox="0 0 180 90" className="overflow-visible">
      <defs>
        <linearGradient id="orb-grad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="rgba(0,229,255,0)" />
          <stop offset="50%" stopColor="rgba(0,229,255,0.8)" />
          <stop offset="100%" stopColor="rgba(0,229,255,0)" />
        </linearGradient>
      </defs>
      {/* Long arc */}
      <path
        d="M 5 55 Q 90 -10 175 55"
        fill="none"
        stroke="url(#orb-grad)"
        strokeWidth="1.25"
        strokeDasharray="260"
        strokeDashoffset={visible ? 0 : 260}
        style={{ transition: "stroke-dashoffset 1400ms cubic-bezier(0.22,1,0.36,1)" }}
      />
      {/* Inner arc */}
      <path
        d="M 30 60 Q 90 20 150 60"
        fill="none"
        stroke="rgba(0,229,255,0.35)"
        strokeWidth="1"
        strokeDasharray="180"
        strokeDashoffset={visible ? 0 : 180}
        style={{ transition: "stroke-dashoffset 1700ms cubic-bezier(0.22,1,0.36,1) 200ms" }}
      />
      {/* Orbital dot */}
      <circle
        cx="90"
        cy="20"
        r="3"
        fill="#00E5FF"
        style={{
          filter: "drop-shadow(0 0 6px rgba(0,229,255,0.9))",
          opacity: visible ? 1 : 0,
          transition: "opacity 600ms ease 1200ms",
          animation: visible ? "gtl-orb-pulse 2.2s ease-in-out infinite 1.4s" : "none",
        }}
      />
      {/* Endpoint nodes */}
      <circle cx="5" cy="55" r="2" fill="rgba(0,229,255,0.6)" />
      <circle cx="175" cy="55" r="2" fill="rgba(0,229,255,0.6)" />
    </svg>
  </div>
);

/* ---------- Wave ---------- */
const Wave = ({ visible }) => (
  <div className="absolute inset-0 overflow-hidden">
    <svg viewBox="0 0 1200 110" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
      <defs>
        <linearGradient id="wave-grad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="rgba(0,229,255,0)" />
          <stop offset="40%" stopColor="rgba(0,229,255,0.55)" />
          <stop offset="60%" stopColor="rgba(0,174,239,0.55)" />
          <stop offset="100%" stopColor="rgba(0,122,255,0)" />
        </linearGradient>
      </defs>
      <path
        d="M 0 60 C 200 10, 400 110, 600 60 S 1000 10, 1200 60"
        fill="none"
        stroke="url(#wave-grad)"
        strokeWidth="1.5"
        strokeDasharray="1400"
        strokeDashoffset={visible ? 0 : 1400}
        style={{ transition: "stroke-dashoffset 1800ms cubic-bezier(0.22,1,0.36,1)" }}
      />
      <path
        d="M 0 55 C 200 5, 400 105, 600 55 S 1000 5, 1200 55"
        fill="none"
        stroke="rgba(0,229,255,0.12)"
        strokeWidth="1"
        strokeDasharray="1400"
        strokeDashoffset={visible ? 0 : 1400}
        style={{ transition: "stroke-dashoffset 2200ms cubic-bezier(0.22,1,0.36,1) 200ms" }}
      />
    </svg>
  </div>
);

/* ---------- Particles ---------- */
const Particles = ({ visible }) => {
  // stable pseudo-random positions
  const parts = Array.from({ length: 18 }).map((_, i) => ({
    left: (i * 53) % 100,
    delay: (i * 230) % 3000,
    duration: 4500 + ((i * 370) % 3000),
    size: i % 4 === 0 ? 3 : 2,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* subtle center hairline */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-px w-[min(900px,80%)] bg-gradient-to-r from-transparent via-[var(--border-soft)] to-transparent" />
      {parts.map((p, i) => (
        <span
          key={i}
          className="absolute bottom-0 rounded-full"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            background: "rgba(0,229,255,0.85)",
            boxShadow: "0 0 8px rgba(0,229,255,0.7)",
            animation: visible
              ? `gtl-particle-rise ${p.duration}ms linear ${p.delay}ms infinite`
              : "none",
            opacity: 0,
          }}
        />
      ))}
    </div>
  );
};

/* ---------- Diagonal ---------- */
const Diagonal = ({ visible }) => (
  <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
    <div className="relative w-[min(1100px,92%)] flex items-center">
      {/* left tick block */}
      <div
        className="flex items-center gap-1"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateX(0)" : "translateX(-12px)",
          transition: "all 700ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <span className="w-1.5 h-1.5 bg-cyan-400" />
        <span className="w-6 h-px bg-cyan-400/60" />
        <span className="text-[9px] mono-label text-cyan-400/80 tracking-[0.3em]">NODE</span>
      </div>
      {/* diagonal line */}
      <div className="relative flex-1 mx-3 h-6 overflow-hidden">
        <div
          className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/70 to-transparent"
          style={{
            transform: `translateY(-50%) scaleX(${visible ? 1 : 0})`,
            transformOrigin: "left",
            transition: "transform 1100ms cubic-bezier(0.22,1,0.36,1) 150ms",
          }}
        />
        <div
          className="absolute top-0 bottom-0 w-[2px]"
          style={{
            left: visible ? "100%" : "0%",
            background: "rgba(0,229,255,0.9)",
            boxShadow: "0 0 18px rgba(0,229,255,0.8)",
            transition: "left 1100ms cubic-bezier(0.22,1,0.36,1) 150ms",
          }}
        />
      </div>
      {/* right block — serial */}
      <div
        className="flex items-center gap-2 text-[9px] mono-label text-faint tracking-[0.3em]"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateX(0)" : "translateX(12px)",
          transition: "all 700ms cubic-bezier(0.22,1,0.36,1) 200ms",
        }}
      >
        <span>SER · {(Math.floor(visible ? 2048 : 0)).toString(16).toUpperCase().padStart(4, "0")}</span>
        <span className="w-6 h-px bg-cyan-400/60" />
        <span className="w-1.5 h-1.5 bg-cyan-400" />
      </div>
    </div>
  </div>
);

/* ---------- Pulse (radar) ---------- */
const Pulse = ({ visible }) => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="relative w-20 h-20 flex items-center justify-center">
      {/* center dot */}
      <span
        className="absolute w-2 h-2 rounded-full bg-cyan-400"
        style={{
          boxShadow: "0 0 16px rgba(0,229,255,0.9)",
          opacity: visible ? 1 : 0,
          transition: "opacity 500ms ease",
        }}
      />
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="absolute rounded-full border border-cyan-400/60"
          style={{
            width: 16,
            height: 16,
            animation: visible ? `gtl-radar 2.6s ease-out ${i * 800}ms infinite` : "none",
          }}
        />
      ))}
      {/* left/right hairlines */}
      <span className="absolute top-1/2 right-full mr-3 h-px w-32 bg-gradient-to-l from-cyan-400/40 to-transparent" />
      <span className="absolute top-1/2 left-full ml-3 h-px w-32 bg-gradient-to-r from-cyan-400/40 to-transparent" />
    </div>
  </div>
);

export default SectionTransition;
